import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as api from '@owl-nest/api-client/latest'

import { t, tn } from '@owl-nest/localize'
import * as hooks from '@owl-nest/hooks'
import * as models from '@owl-nest/models'
import { CATEGORY, EVENT_TYPE, SITE, useTracking } from '@owl-nest/shadow'

import { getCurrentLocale } from '../router'
import { useDisplayMode } from '../hooks/useDisplayMode'
import * as UFE from '../UFE'

type GoalCardProps = {
  project: models.project.ProjectForOwner
}

export function GoalCard({ project }: GoalCardProps): React.ReactElement {
  const isMember = models.project.isUserMember(project)
  const router = hooks.useNextRouter()
  const currentLocale = getCurrentLocale(router)
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const subtitle = models.project.subtitle(project, currentLocale)
  const tracking = useTracking()

  return (
    <GoalBlock isMember={isMember}>
      <plume.StatusTag>
        <plume.glyphs.crowdfunding.EggFull size={18} />
        <plume.styles.copy.S>{t('Next goal')}</plume.styles.copy.S>
      </plume.StatusTag>

      {isEditMode && (
        <EditButtonWrapper href={`/projects/${project.id}/backoffice/memberships/`}>
          <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
        </EditButtonWrapper>
      )}

      {subtitle ? (
        <plume.styles.heading.XXXS>{subtitle}</plume.styles.heading.XXXS>
      ) : isEditMode ? (
        <plume.styles.heading.XXXS>{t('Set goal')}</plume.styles.heading.XXXS>
      ) : null}
      {project.subscriptions_count !== undefined && (
        <plume.ProgressBar
          data={
            !project.show_goal
              ? tn('%(memberCount)s member', '%(memberCount)s members', project.subscriptions_count, {
                  memberCount: plume.accounting.formatNumber(project.subscriptions_count),
                })
              : project.goal_type === api.GoalType.QUANTITATIVE
                ? `${plume.accounting.formatNumber(project.subscriptions_count)}\u00a0<span>/\u00a0${tn(
                    '%(memberCount)s member',
                    '%(memberCount)s members',
                    project.goal,
                    {
                      memberCount: plume.accounting.formatNumber(project.goal),
                    },
                  )}</span>`
                : `${
                    project.subscriptions_amount
                      ? plume.accounting.localeMoney(project.subscriptions_amount, {
                          precision: 0,
                          rates: UFE.CURRENCIES.RATES,
                          currency: project.currency,
                          targetCurrency: project.currency,
                          lang: UFE.USER_LOCALE ?? UFE.DEFAULT_LOCALE,
                        })
                      : 0
                  }\u00a0<span>/\u00a0${plume.accounting.localeMoney(project.goal, {
                    precision: 0,
                    rates: UFE.CURRENCIES.RATES,
                    currency: project.currency,
                    targetCurrency: project.currency,
                    lang: UFE.USER_LOCALE ?? UFE.DEFAULT_LOCALE,
                  })}\u00a0${t('per month')}
            `
          }
          width={models.project.progress(project)}
        />
      )}

      {!isMember && (
        <plume.RoundedButton
          onClick={trackAndRedirect}
          fitToParent
          kind="primary"
          icon={<plume.glyphs.stroke.Plus size={18} />}
        >
          {t('Become a member')}
        </plume.RoundedButton>
      )}
    </GoalBlock>
  )

  function trackAndRedirect(): void {
    tracking.track({
      event: EVENT_TYPE.GENERIC,
      eventCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventAction: 'ClickBecomeMember',
      siteCategory: SITE.ULULE,
      pageCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventCallback: () => {
        if (typeof window !== 'undefined') {
          window.location.href = '#subscriptions'
        }
      },
    })
  }
}

const EditButtonWrapper = styled.a`
  display: contents;
`

const GoalBlock = styled.div<{ isMember?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-inline: 16px;
  max-width: 548px;
  padding: 30px 16px 24px 16px;
  border-radius: 4px;
  margin-top: 40px;
  margin-bottom: ${(props) => (props.isMember ? '16px' : '40px')};
  background: ${plume.COLORS.PRIMARY_GREY_000};
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  position: relative;
  text-align: center;

  @media screen and (min-width: 585px) {
    width: 100%;
    margin-inline: auto;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: 48px;
    margin-bottom: ${(props) => (props.isMember ? '16px' : '0px')};
  }

  ${plume.StatusTag} {
    display: flex;
    position: absolute;
    top: -15px;
    padding: 2px 8px;
    outline: 3px solid ${plume.COLORS.PRIMARY_GREY_000};
    border-radius: 4px;

    &::first-letter {
      text-transform: capitalize;
    }

    ${plume.glyphs.crowdfunding.EggFull} {
      top: 3px;
      margin-right: 8px;

      g {
        fill: ${(props) => props.theme?.colors.primary ?? plume.COLORS.PRIMARY_BLUE_500};
      }
    }
  }

  ${plume.EditButton} {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  ${plume.RoundedButton} {
    margin-top: 8px;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      margin-top: 4px;
      width: fit-content;
    }
  }
`
